import React, { useState, useEffect } from "react";
import styled from "styled-components";
import logo from "../../assets/olasoftlogo.png";
import logoWhite from "../../assets/olasoftlogo_whit.png";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredSocial, setHoveredSocial] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const socialLinks = [
    { icon: 'in', url: 'https://linkedin.com' },
    { icon: 'f', url: 'https://facebook.com' },
    { icon: '𝕏', url: 'https://x.com' },
  ];

  return (
    <>
      <Header $scrolled={scrolled} id="header">
        <HeaderContainer $scrolled={scrolled}>
          <Logo>
            {/* Logo blanc au départ */}
            <LogoImg 
              src={logoWhite} 
              alt="Olasoft logo" 
              $scrolled={scrolled}
              className={scrolled ? "hidden" : "visible"}
            />
            {/* Logo normal après scroll */}
            <LogoImg 
              src={logo} 
              alt="Olasoft logo" 
              $scrolled={scrolled}
              className={scrolled ? "visible" : "hidden"}
            />
          </Logo>
          
          <Nav>
            <NavMenu $scrolled={scrolled}>
              <li><NavLink $scrolled={scrolled} href="#accueil">A propos</NavLink></li>
              <li><NavLink $scrolled={scrolled} href="#presentation">Présentation</NavLink></li>
              <li><NavLink $scrolled={scrolled} href="#services">Services</NavLink></li>
              <li><NavLink $scrolled={scrolled} href="#realisations">Réalisations</NavLink></li>
              <li><NavLink $scrolled={scrolled} href="#temoignages">Partenaire</NavLink></li>
              <li><NavLink $scrolled={scrolled} href="#contact">Contact</NavLink></li>
            </NavMenu>

            {/* Icônes sociales identiques au footer */}
            <SocialIcons $scrolled={scrolled}>
              {socialLinks.map((social, index) => (
                <SocialIconLink
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  $scrolled={scrolled}
                  $isHovered={hoveredSocial === index}
                  onMouseEnter={() => setHoveredSocial(index)}
                  onMouseLeave={() => setHoveredSocial(null)}
                >
                  {social.icon}
                </SocialIconLink>
              ))}
            </SocialIcons>
            
            <Hamburger 
              onClick={toggleMobileMenu}
              $isActive={mobileMenuOpen}
              $scrolled={scrolled}
            >
              <span></span>
              <span></span>
              <span></span>
            </Hamburger>
          </Nav>
        </HeaderContainer>
        
        <MobileMenu $isOpen={mobileMenuOpen}>
          <li><MobileLink href="#accueil" onClick={closeMobileMenu}>Accueil</MobileLink></li>
          <li><MobileLink href="#presentation" onClick={closeMobileMenu}>Présentation</MobileLink></li>
          <li><MobileLink href="#services" onClick={closeMobileMenu}>Services</MobileLink></li>
          <li><MobileLink href="#realisations" onClick={closeMobileMenu}>Réalisations</MobileLink></li>
          <li><MobileLink href="#temoignages" onClick={closeMobileMenu}>Partenaire</MobileLink></li>
          <li><MobileLink href="#contact" onClick={closeMobileMenu}>Contact</MobileLink></li>
          
          {/* Réseaux sociaux dans le menu mobile */}
          <MobileSocialIcons>
            {socialLinks.map((social, index) => (
              <MobileSocialLink 
                key={index}
                href={social.url} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                <MobileSocialIcon $isHovered={hoveredSocial === index}>
                  {social.icon}
                </MobileSocialIcon>
                {social.icon === 'in' && 'LinkedIn'}
                {social.icon === 'f' && 'Facebook'}
                {social.icon === '𝕏' && 'X'}
              </MobileSocialLink>
            ))}
          </MobileSocialIcons>
        </MobileMenu>
      </Header>
    </>
  );
};

export default Navbar;

// Styles modifiés - utilisation du préfixe $ pour les props transient
const Logo = styled.div`
  display: flex;
  align-items: center;
  padding-left: 25px;
  position: relative;
  height: 45px;

  @media (max-width: 480px) {
    padding-left: 20px;
    height: 32px;
  }
`;

const LogoImg = styled.img`
  height: 45px;
  transition: all 0.3s ease;
  position: absolute;
  top: 0;
  
  /* Suppression de tous les filtres pour garder les couleurs originales */
  filter: none;
  
  &.visible {
    opacity: 1;
    transform: scale(1);
  }
  
  &.hidden {
    opacity: 0;
    transform: scale(0.8);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    height: 38px;
  }
  
  @media (max-width: 480px) {
    height: 32px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NavMenu = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 0;
  flex: 1;

  @media (max-width: 992px) {
    display: none;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  padding: 15px 20px;
  display: block;
  font-size: 1.0416vw;
  font-weight: 600;
  transition: color 0.3s;
  position: relative;
  overflow: hidden;
  color: ${props => 
    props.$scrolled 
      ? "#000"
      : "#fff"};
  font-family: "Raleway";
  text-align: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: #1a237e; /* Bleu foncé au lieu d'orange */
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease-out;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }

  &:first-child {
    color: ${props => 
      props.$scrolled 
        ? "#000"
        : "#fff"};
    border-radius: 8px;
    margin-right: -5px;
    margin-left: -5px;
    overflow: visible;
  }

  &:first-child::after {
    display: none;
  }
  
  @media (max-width: 1200px) {
    padding: 12px 15px;
    font-size: 13px;
  }
`;

// Nouveaux styles pour les icônes sociales identiques au footer
const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 25px;

  @media (max-width: 992px) {
    display: none;
  }
`;

const SocialIconLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => 
    props.$scrolled 
      ? props.$isHovered 
        ? 'rgba(26, 35, 126, 0.7)' /* Bleu foncé */
        : 'rgba(26, 35, 126, 0.5)' /* Bleu foncé */
      : props.$isHovered 
        ? 'rgba(255, 255, 255, 0.3)' 
        : 'rgba(255, 255, 255, 0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: ${props => props.$scrolled ? "#ffffff" : "#ffffff"};
  font-size: 14px;
  font-weight: 600;
  font-family: "Raleway", sans-serif;
  transform: ${props => props.$isHovered ? 'translateY(-2px)' : 'translateY(0)'};

  &:hover {
    background: ${props => 
      props.$scrolled 
        ? 'rgba(26, 35, 126, 0.7)' /* Bleu foncé */
        : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
`;

const Hamburger = styled.button`
  display: none;
  padding: 10px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 10;

  @media (max-width: 992px) {
    display: block;
  }

  span {
    display: block;
    width: 25px;
    height: 3px;
    margin: 5px 0;
    background-color: ${props => 
      props.$scrolled 
        ? "#000"
        : "#fff"};
    transition: transform 0.3s, background-color 0.3s;
  }

  ${props => 
    props.$isActive && `
      span:nth-child(1) { 
        transform: translateY(8px) rotate(45deg); 
      }
      span:nth-child(2) { 
        opacity: 0; 
      }
      span:nth-child(3) { 
        transform: translateY(-8px) rotate(-45deg); 
      }
    `}
`;

const MobileMenu = styled.ul`
  display: ${props => (props.$isOpen ? "block" : "none")};
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: #fff;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 0;
  padding: 0;

  @media (min-width: 993px) {
    display: none;
  }
`;

const MobileLink = styled.a`
  display: block;
  padding: 15px 25px;
  text-decoration: none;
  color: #333;
  border-bottom: 1px solid #f1f1f1;
  font-weight: 500;
  border-left: 5px solid transparent;
  transition: background-color 0.2s, border-left 0.2s;
  font-family: 'Raleway';
  text-align: center;

  &:hover {
    background-color: #f1f1f1;
    border-left: 5px solid #1a237e; /* Bleu foncé au lieu d'orange */
  }

  &:first-child {
    background-color: #1a237e; /* Bleu foncé au lieu d'orange */
    color: #fff;
    border-left: 5px solid #1a237e; /* Bleu foncé */
  }
`;

const MobileSocialIcons = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px 25px;
  border-top: 1px solid #f1f1f1;
  background: #f9f9f9;
`;

const MobileSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: color 0.3s ease;
  font-family: 'Raleway';

  &:hover {
    color: #1a237e; /* Bleu foncé au lieu d'orange */
  }

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const MobileSocialIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => 
    props.$isHovered 
      ? 'rgba(26, 35, 126, 0.7)' /* Bleu foncé */
      : 'rgba(26, 35, 126, 0.5)'}; /* Bleu foncé */
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
`;

const Header = styled.header`
  position: fixed;
  top: 1.91vw;
  padding: 0 5.448vw;
  width: 100%;
  z-index: 9999;
  opacity: 1;
  transition: all 0.5s ease-in-out;

  /* Responsive pour différents écrans */
  @media (max-width: 768px) {
    top: 1.5vw;
    padding: 0 4vw;
  }

  @media (max-width: 480px) {
    top: 1vw;
    padding: 0 3vw;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0.448vw;
  border-radius: 40px;
  overflow: hidden;
  background-color: ${props => 
    props.$scrolled 
      ? "#fff" 
      : "rgba(255, 255, 255, 0.15)"};
  backdrop-filter: ${props => 
    !props.$scrolled 
      ? "blur(10px)" 
      : "none"};
  box-shadow: ${props => 
    props.$scrolled 
      ? "0 4px 10px rgba(0, 0, 0, 0.1)" 
      : "0 4px 15px rgba(0, 0, 0, 0.2)"};
  transition: background-color 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease;

  /* Ajustements responsive pour le container */
  @media (max-width: 768px) {
    padding: 10px 4vw;
    border-radius: 35px;
    margin: 0 1.5vw;
  }

  @media (max-width: 480px) {
    padding: 8px 3vw;
    border-radius: 30px;
    margin: 0 1vw;
  }
`;